import { playNoise, playTone, type AudioVoice } from './synth';

const BPM = 112;
const EIGHTH = 60 / BPM / 2;
const LOOKAHEAD = 0.1;
// An original four-bar A-minor figure: soft plucks over a bass pulse, with a lighter
// answering phrase each second bar. Tension adds percussion without changing tempo.
const CHORDS = [
  { root: 45, third: 3 },
  { root: 41, third: 4 },
  { root: 48, third: 4 },
  { root: 43, third: 4 },
];
const FIGURE = [0, 2, 1, 2, 3, 2, 1, 2];
const frequency = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);

/** Audio-clock scheduling avoids tying the beat to game frame rate. */
export function createMusicLoop(ctx: AudioContext, destination: AudioNode) {
  let timer: ReturnType<typeof setInterval> | undefined;
  let nextNote = 0;
  let step = 0;
  let tension = 0;
  let voices: AudioVoice[] = [];

  function schedule() {
    const now = ctx.currentTime;
    if (ctx.state !== 'running') {
      nextNote = now + 0.025;
      return;
    }
    voices = voices.filter((sound) => sound.endsAt > now);
    // Background throttling must not queue an entire missed song at once.
    if (nextNote < now - LOOKAHEAD) nextNote = now + 0.025;
    while (nextNote < now + LOOKAHEAD) {
      const chord = CHORDS[Math.floor(step / 8) % CHORDS.length]!;
      const tones = [0, chord.third, 7, 12];
      const note = chord.root + 12 + tones[FIGURE[step % FIGURE.length]!]!;
      voices.push(playTone(ctx, 'triangle', frequency(note), 0.17, 0.035, destination, nextNote));
      if (step % 2 === 0) {
        const bass = chord.root + (step % 8 === 4 ? 7 : 0);
        voices.push(playTone(ctx, 'sine', frequency(bass), 0.3, 0.075, destination, nextNote));
      }
      if (step % 4 === 0) voices.push(playTone(ctx, 'sine', 55, 0.07, 0.06, destination, nextNote));
      if (step % 4 === 2 || (tension > 0.5 && step % 2 === 1)) {
        voices.push(playNoise(ctx, 0.035, 0.012 + tension * 0.012, destination, nextNote));
      }
      if (tension > 0.75 && step % 4 === 3) {
        voices.push(
          playTone(ctx, 'sine', frequency(note + 12), 0.09, 0.018, destination, nextNote),
        );
      }
      nextNote += EIGHTH;
      step += 1;
    }
  }

  return {
    start() {
      if (timer !== undefined) return;
      nextNote = ctx.currentTime + 0.025;
      schedule();
      timer = setInterval(schedule, 25);
    },
    stop() {
      if (timer !== undefined) clearInterval(timer);
      timer = undefined;
      for (const sound of voices) sound.stop();
      voices = [];
      step = 0;
    },
    setTension(value: number) {
      tension = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
    },
  };
}
